import { API_BASE, apiUrl } from './api';
import { clearAuth, isSignedIn, readAuth } from '../hooks/useAuthStorage';

/**
 * Fired once when the backend rejects a request from a signed-in user.
 * `useSessionExpiry` listens for it and shows the "logged out" alert.
 */
export const SESSION_EXPIRED_EVENT = 'bansal-attire:session-expired';

/** Statuses the backend uses for "your session is no longer valid". */
const AUTH_FAILURE_STATUSES = [401, 403, 419];

/**
 * Endpoints where a 401/403 means "wrong credentials / bad OTP", not
 * "your session expired". Logging the visitor out on those would be
 * both wrong and confusing — they are not signed in yet.
 */
const PUBLIC_AUTH_PATHS = [
  '/api/login',
  '/api/user/signup',
  '/api/send-otp',
  '/api/verify-otp',
  '/api/guest-register',
  '/api/forgot-password',
];

// One expiry per session: several requests usually fail together (the
// header, the cart and the page body all call the API at once) and the
// user should still see a single alert.
let expiryAnnounced = false;
let installed = false;

/** Pull a comparable URL string out of any of fetch's argument shapes. */
const resolveUrl = (input) => {
  if (typeof input === 'string') return input;
  if (input instanceof URL) return input.href;
  if (input && typeof input.url === 'string') return input.url;
  return '';
};

const isOurApi = (url) => url.startsWith(API_BASE) || url.startsWith('/api/');

const isPublicAuthEndpoint = (url) =>
  PUBLIC_AUTH_PATHS.some((path) => url.includes(path));

/**
 * Drops the stored credentials and asks the UI to tell the user.
 * Exported so a caller that detects an expired session in its own way
 * (a `{ success: false }` body, say) can reuse the same flow.
 */
export const expireSession = () => {
  if (expiryAnnounced) return;
  expiryAnnounced = true;

  clearAuth();

  // Deferred by a macrotask on purpose. Most call sites answer a failed
  // request with their own alert ("Could not add to cart", ...) from a
  // `.catch`, which runs in a microtask right after this. Announcing the
  // expiry afterwards means the logout alert is the one left on screen
  // instead of being replaced by a less useful message.
  setTimeout(() => {
    window.dispatchEvent(new Event(SESSION_EXPIRED_EVENT));
  }, 0);
};

/**
 * Asks the backend whether the stored token is still accepted, so a
 * logout that happened on the server (token expired, revoked, user
 * removed) shows up here even on pages that never call a protected
 * endpoint. A 401 is handled by the fetch guard below; network errors
 * and 5xx are ignored, since being offline is not being logged out.
 */
export const verifySession = async () => {
  const { token, user_id } = readAuth();
  if (!token || !user_id) return;

  try {
    const response = await fetch(apiUrl('/api/user-check'), {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ user_id }),
    });
    if (!response.ok) return;

    const data = await response.json().catch(() => null);
    // Only act if this is still the session we asked about.
    if (data?.success === false && readAuth().token === token) expireSession();
  } catch {
    // Offline or the server is down; try again on the next check.
  }
};

/** Lets the user sign in again after an expiry without a page reload. */
export const resetSessionExpiry = () => {
  expiryAnnounced = false;
};

/**
 * Wraps `window.fetch` so every API call in the app is checked for an
 * auth failure. The alternative was repeating the same status check in
 * all ~47 call sites, which is exactly how they drifted apart before.
 */
export const installSessionGuard = () => {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  const originalFetch = window.fetch.bind(window);

  window.fetch = async (input, init) => {
    const response = await originalFetch(input, init);

    if (
      AUTH_FAILURE_STATUSES.includes(response.status) &&
      isSignedIn() &&
      isOurApi(resolveUrl(input)) &&
      !isPublicAuthEndpoint(resolveUrl(input))
    ) {
      expireSession();
    }

    return response;
  };
};

export default installSessionGuard;
